/**
 * The desktop client, seen from inside the web client.
 *
 * `app-electron/` runs this same client inside a window and puts a very small
 * object on `window` so the two can cooperate on the things a page cannot do
 * for itself: a count on the taskbar icon, and bringing the window forward
 * when somebody clicks a notification.
 *
 * It is read defensively. The bridge is injected by whatever is hosting the
 * page, and a browser extension can put an object of that name on `window`
 * just as easily, so nothing is trusted beyond "these two are functions" — and
 * nothing is ever read back out of it. In an ordinary browser it is simply
 * absent, and every caller here is a no-op.
 */

export interface UnreadReport {
  /** Unread messages across every conversation. */
  unread: number
  /** Of those, the ones that named the reader. */
  mentions: number
}

export interface DesktopBridge {
  platform: 'electron'
  version: string
  meetingWindows?: boolean
  setUnread(report: UnreadReport): void
  focus(): void
}

function looksLikeBridge(value: unknown): value is DesktopBridge {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<DesktopBridge>
  return (
    candidate.platform === 'electron' &&
    typeof candidate.setUnread === 'function' &&
    typeof candidate.focus === 'function'
  )
}

export function desktopBridge(): DesktopBridge | null {
  if (typeof window === 'undefined') return null
  const candidate = (window as { relaiiDesktop?: unknown }).relaiiDesktop
  return looksLikeBridge(candidate) ? candidate : null
}

/** True when this page is running inside the relaii desktop client. */
export function isDesktop(): boolean {
  return desktopBridge() !== null
}

/**
 * A browser on a computer: a mouse or trackpad, and not a phone or tablet.
 * iPadOS asks for desktop sites as a Mac, so a "Mac" with a touch screen is
 * taken for what it is.
 */
function desktopLikeBrowser(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  if (!window.matchMedia('(pointer: fine)').matches) return false
  if (typeof navigator === 'undefined') return false
  const hinted = (navigator as { userAgentData?: { mobile?: unknown } }).userAgentData?.mobile
  if (hinted === true) return false
  const agent = navigator.userAgent
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(agent)) return false
  if (/Macintosh/.test(agent) && navigator.maxTouchPoints > 1) return false
  return true
}

/**
 * Whether a meeting can be moved to a window of its own. Older desktop hosts
 * refuse child windows. A phone's browser opens one as another tab and puts
 * the tab that holds the camera and microphone in the background.
 */
export function supportsMeetingWindows(): boolean {
  const bridge = desktopBridge()
  return bridge === null ? desktopLikeBrowser() : bridge.meetingWindows === true
}

/** Tells the host what to put on its badge. Silent in a browser. */
export function reportUnread(report: UnreadReport): void {
  try {
    desktopBridge()?.setUnread(report)
  } catch {
    // A host that throws is a host that does not get told again this tick.
    // It must never take the conversation down with it.
  }
}

/** Asks the host to bring its window forward. Silent in a browser. */
export function focusDesktopWindow(): void {
  try {
    desktopBridge()?.focus()
  } catch {
    // As above.
  }
}
